import * as actionTypes from "./constants";
import * as Service from "./services";
import { normalizeGroupedChartRows } from "./components/charts/groupedAssetChart";
import { normalizeHorizontalChartRows } from "./components/charts/horizontalAssetChart";
import { normalizeWeightedConditionRows } from "./components/charts/weightedAverage";

const updateAssetManagementEntityParams = entityParams => {
    return async dispatch => {
        try {
            if (entityParams) {
                dispatch({
                    type: actionTypes.UPDATE_ASSET_MANAGEMENT_ENTITY_PARAMS_SUCCESS,
                    response: entityParams
                });
            }
        } catch (e) {
            dispatch({
                type: actionTypes.UPDATE_ASSET_MANAGEMENT_ENTITY_PARAMS_FAILURE,
                error: entityParams
            });
        }
    };
};

const getChartData = (params, chartKey) => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.GET_CHARTS_DETAILS_REQUEST, key: chartKey });
            const res = await Service.getChartData(params, chartKey);

            if (res && res.status === 200) {
                const chartData = res.data;
                if (chartData.success) {
                    dispatch({ type: actionTypes.GET_CHARTS_DETAILS_SUCCESS, response: chartData, key: chartKey });
                } else {
                    dispatch({ type: actionTypes.GET_CHARTS_DETAILS_FAILURE, error: chartData, key: chartKey });
                }
            } else {
                dispatch({
                    type: actionTypes.GET_CHARTS_DETAILS_FAILURE,
                    error: res.response && res.response.data,
                    key: chartKey
                });
            }
        } catch (e) {
            dispatch({
                type: actionTypes.GET_CHARTS_DETAILS_FAILURE,
                error: e.response && e.response.data,
                key: chartKey
            });
        }
    };
};

const getEndOfLifeGroupedChart = (params, chartKey = "end_of_life_chart") => {
    return async dispatch => {
        try {
            const SLOT_ONE_ROWS_KEY = { end_of_life_chart: "end_of_life_chart", assets_age_chart: "age_chart" };
            dispatch({ type: actionTypes.GET_CHARTS_DETAILS_REQUEST, key: chartKey });
            const res = await Service.getGroupedAssetChart(params);
            if (res && res.status === 200) {
                dispatch({
                    type: actionTypes.GET_CHARTS_DETAILS_SUCCESS,
                    response: {
                        success: true,
                        [SLOT_ONE_ROWS_KEY[chartKey] || chartKey]: normalizeGroupedChartRows(res.data, params?.chart_type),
                        ...(chartKey === "end_of_life_chart" ? { double_donut_chart: res.data?.double_donut_chart || null } : {})
                    },
                    key: chartKey
                });
            } else {
                dispatch({ type: actionTypes.GET_CHARTS_DETAILS_FAILURE, error: res?.response?.data, key: chartKey });
            }
        } catch (e) {
            dispatch({ type: actionTypes.GET_CHARTS_DETAILS_FAILURE, error: e.response && e.response.data, key: chartKey });
        }
    };
};

// Same /assets/charts endpoint as the grouped slot-one chart, extended with horizontal_chart_type
const getHorizontalAssetChart = params => {
    return async dispatch => {
        const chartKey = "horizontal_chart";
        try {
            dispatch({ type: actionTypes.GET_CHARTS_DETAILS_REQUEST, key: chartKey });
            const res = await Service.getGroupedAssetChart(params);
            if (res && res.status === 200) {
                dispatch({
                    type: actionTypes.GET_CHARTS_DETAILS_SUCCESS,
                    response: { success: true, horizontal_chart: normalizeHorizontalChartRows(res.data, params?.chart_type) },
                    key: chartKey
                });
            } else {
                dispatch({ type: actionTypes.GET_CHARTS_DETAILS_FAILURE, error: res?.response?.data, key: chartKey });
            }
        } catch (e) {
            dispatch({ type: actionTypes.GET_CHARTS_DETAILS_FAILURE, error: e.response && e.response.data, key: chartKey });
        }
    };
};

const getWeightedAverageChart = params => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.GET_WEIGHTED_AVERAGE_CHART_REQUEST });
            const res = await Service.getWeightedConditionChart(params);
            if (res && res.status === 200) {
                dispatch({
                    type: actionTypes.GET_WEIGHTED_AVERAGE_CHART_SUCCESS,
                    response: { success: true, weighted_average: normalizeWeightedConditionRows(res.data) }
                });
            } else {
                dispatch({ type: actionTypes.GET_WEIGHTED_AVERAGE_CHART_FAILURE, error: res?.response?.data });
            }
        } catch (e) {
            dispatch({ type: actionTypes.GET_WEIGHTED_AVERAGE_CHART_FAILURE, error: e.response && e.response.data });
        }
    };
};

const getAssetGaugeChart = params => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.GET_ASSET_GAUGE_CHART_REQUEST });
            const res = await Service.getAssetGaugeChart(params);
            if (res && res.status === 200) {
                dispatch({ type: actionTypes.GET_ASSET_GAUGE_CHART_SUCCESS, response: res.data });
            } else {
                dispatch({ type: actionTypes.GET_ASSET_GAUGE_CHART_FAILURE, error: res?.data || res?.response?.data });
            }
        } catch (e) {
            dispatch({ type: actionTypes.GET_ASSET_GAUGE_CHART_FAILURE, error: e.response && e.response.data });
        }
    };
};

const getFilterLists = (filterKey, params) => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.GET_FILTER_LISTS_REQUEST });
            const res = await Service.getFilterLists(filterKey, params);
            if (res && res.status === 200) {
                const responseData = res.data;
                dispatch({
                    type: actionTypes.GET_FILTER_LISTS_SUCCESS,
                    filterKey,
                    response: responseData
                });
            } else {
                dispatch({
                    type: actionTypes.GET_FILTER_LISTS_FAILURE,
                    error: res.response.data
                });
            }
        } catch (e) {
            dispatch({
                type: actionTypes.GET_FILTER_LISTS_FAILURE,
                error: e.response && e.response.data
            });
        }
    };
};
const getSfciChart = param => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.GET_SFCI_CHART_REQUEST });
            const res = await Service.getSfciChart(param);
            if (res && res.status === 200) {
                const codeData = res.data;
                dispatch({ type: actionTypes.GET_SFCI_CHART_SUCCESS, response: codeData });
            } else {
                dispatch({ type: actionTypes.GET_SFCI_CHART_FAILURE, error: res.response.data });
            }
        } catch (e) {
            dispatch({ type: actionTypes.GET_SFCI_CHART_FAILURE, error: e.response && e.response.data });
        }
    };
};

const getAssetDashboardMap = params => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.GET_ASSET_DASHBOARD_MAP_REQUEST });
            const res = await Service.getAssetDashboardMap(params);
            if (res && res.status === 200) {
                const codeData = res.data;
                if (codeData.success) {
                    dispatch({ type: actionTypes.GET_ASSET_DASHBOARD_MAP_SUCCESS, response: codeData });
                } else {
                    dispatch({ type: actionTypes.GET_ASSET_DASHBOARD_MAP_FAILURE, error: codeData });
                }
            } else {
                dispatch({
                    type: actionTypes.GET_ASSET_DASHBOARD_MAP_FAILURE,
                    error: res.response && res.response.data
                });
            }
        } catch (e) {
            dispatch({
                type: actionTypes.GET_ASSET_DASHBOARD_MAP_FAILURE,
                error: e.response && e.response.data
            });
        }
    };
};

const saveChartPopup = data => {
    return async dispatch => {
        dispatch({
            type: actionTypes.SAVE_CHART_POPUP,
            response: data
        });
    };
};
const saveChartTab = data => {
    return async dispatch => {
        dispatch({
            type: actionTypes.SAVE_CHART_TAB,
            response: data
        });
    };
};

const saveMasterFilters = params => {
    return async dispatch => {
        dispatch({
            type: actionTypes.SAVE_CHART_PARAMS,
            response: params
        });
    };
};
const saveSelectedMasterFilterList = params => {
    return async dispatch => {
        dispatch({
            type: actionTypes.SAVE_SELECTED_MASTER_FILTER_LIST,
            response: params
        });
    };
};
const saveChartView = params => {
    return async dispatch => {
        dispatch({
            type: actionTypes.SAVE_CHART_VIEW,
            response: params
        });
    };
};

const getAssetImportHistory = params => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.GET_ASSET_IMPORT_HISTORY_REQUEST });
            const res = await Service.getAssetImportHistory(params);
            if (res && res.status === 200) {
                const responseData = res.data;
                dispatch({
                    type: actionTypes.GET_ASSET_IMPORT_HISTORY_SUCCESS,
                    response: responseData
                });
            } else {
                dispatch({
                    type: actionTypes.GET_ASSET_IMPORT_HISTORY_FAILURE,
                    error: res.response.data
                });
            }
        } catch (e) {
            dispatch({
                type: actionTypes.GET_ASSET_IMPORT_HISTORY_FAILURE,
                error: e.response && e.response.data
            });
        }
    };
};
const deleteAssetImportHistory = id => {
    return async dispatch => {
        try {
            dispatch({ type: actionTypes.DELETE_ASSET_IMPORT_HISTORY_REQUEST });
            const res = await Service.deleteAssetImportHistory(id);
            if (res && res.status === 200) {
                const responseData = res.data;
                dispatch({
                    type: actionTypes.DELETE_ASSET_IMPORT_HISTORY_SUCCESS,
                    response: responseData
                });
            } else {
                dispatch({
                    type: actionTypes.DELETE_ASSET_IMPORT_HISTORY_FAILURE,
                    error: res.response.data
                });
            }
        } catch (e) {
            dispatch({
                type: actionTypes.DELETE_ASSET_IMPORT_HISTORY_FAILURE,
                error: e.response && e.response.data
            });
        }
    };
};

export default {
    updateAssetManagementEntityParams,
    getChartData,
    getEndOfLifeGroupedChart,
    getHorizontalAssetChart,
    getWeightedAverageChart,
    getAssetGaugeChart,
    getFilterLists,
    saveChartPopup,
    saveChartTab,
    saveMasterFilters,
    saveSelectedMasterFilterList,
    saveChartView,
    getSfciChart,
    getAssetDashboardMap,
    getAssetImportHistory,
    deleteAssetImportHistory
};
